'use client';

import { useEffect, useRef, useState } from 'react';
import { Box, CircularProgress } from '@mui/material';

const PAGE_GAP = 8;

/** Draws every page of a PDF at the box's width. iOS shows a PDF iframe at print size, cropped on the right. */
export default function PdfPages({ url, title }: { url: string; title: string }) {
  const pages = useRef<HTMLDivElement>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'failed'>('loading');

  useEffect(() => {
    const box = pages.current;
    if (!box) return;
    let cancelled = false;
    let destroy: (() => void) | undefined;
    setState('loading');

    (async () => {
      // Loading the worker module sets globalThis.pdfjsWorker, so pdf.js parses on this thread.
      // A worker URL does not bundle here: pdfjs-dist is a server external package.
      await import('pdfjs-dist/legacy/build/pdf.worker.min.mjs');
      const pdfjs = await import('pdfjs-dist/legacy/build/pdf.min.mjs');
      const task = pdfjs.getDocument({ url });
      destroy = () => void task.destroy();
      const pdf = await task.promise;
      const width = box.clientWidth;
      const ratio = window.devicePixelRatio || 1;
      for (let number = 1; number <= pdf.numPages; number++) {
        if (cancelled) return;
        const page = await pdf.getPage(number);
        const viewport = page.getViewport({ scale: (width / page.getViewport({ scale: 1 }).width) * ratio });
        const canvas = document.createElement('canvas');
        canvas.width = Math.floor(viewport.width);
        canvas.height = Math.floor(viewport.height);
        Object.assign(canvas.style, { display: 'block', width: '100%', background: '#fff', marginBottom: `${PAGE_GAP}px` });
        await page.render({ canvas, viewport }).promise;
        if (cancelled) return;
        box.appendChild(canvas);
        if (number === 1) setState('ready');
      }
    })().catch(() => {
      if (!cancelled) setState('failed');
    });

    return () => {
      cancelled = true;
      destroy?.();
      box.replaceChildren();
    };
  }, [url]);

  if (state === 'failed') return <Box component="iframe" src={url} title={title} sx={{ width: '100%', height: '100%', border: 0, bgcolor: '#fff' }} />;

  return (
    <Box sx={{ position: 'relative', width: '100%', height: '100%', overflowY: 'auto', touchAction: 'pan-y pinch-zoom', WebkitOverflowScrolling: 'touch' }}>
      <Box ref={pages} aria-label={title} />
      {state === 'loading' && <CircularProgress size={28} sx={{ color: '#fff', position: 'absolute', top: '40%', left: '50%', ml: '-14px' }} />}
    </Box>
  );
}
